/**
 * useMyCloudClippingsPage Hook
 *
 * 管理云剪藏簿页面的所有状态和业务逻辑
 * 在 Electron 和 Web 端之间共享
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { message } from 'antd';
import type {
  CloudPost,
  CloudBlogger,
  CloudTag,
  CloudTagGroup,
  CloudClippingsView,
} from '../types';
import type { CloudShareParams, CloudShareResponse } from '../apiClient';
import { usePostSelection } from './usePostSelection';
import { shouldForceTimeSort } from '../utils/viewConfig';

// Debounce 延迟时间（毫秒）
const SYNC_DEBOUNCE_DELAY = 3000;

export interface UseMyCloudClippingsPageOptions {
  isLoggedIn: boolean;
  isInitializing: boolean;
  urlView?: string;
  navigate: (path: string, options?: any) => void;
  /** API 客户端（平台特定实现） */
  apiClient: CloudClippingsApiClient;
}

/**
 * API 客户端接口（由平台特定实现提供）
 */
export interface CloudClippingsApiClient {
  // Tags
  getTags: () => Promise<{ tags: CloudTag[] }>;
  createTag: (data: { name: string; groupId?: string }) => Promise<{ tag: CloudTag }>;
  updateTag: (id: string, data: { name?: string; description?: string }) => Promise<{ tag: CloudTag }>;
  deleteTag: (id: string) => Promise<void | { message?: string }>;
  reorderTags: (tagOrders: Array<{ id: string; order: number; groupId?: string | null }>) => Promise<void | { message?: string }>;
  shareTag?: (id: string, params: CloudShareParams) => Promise<CloudShareResponse>;

  // Tag Groups
  getTagGroups: () => Promise<{ tagGroups: CloudTagGroup[] }>;
  createTagGroup: (data: { name: string }) => Promise<{ tagGroup: CloudTagGroup }>;
  updateTagGroup: (id: string, data: { name?: string; description?: string; isExpanded?: boolean }) => Promise<{ tagGroup: CloudTagGroup }>;
  deleteTagGroup: (id: string) => Promise<void | { message?: string }>;
  reorderTagGroups: (groupOrders: Array<{ id: string; order: number }>) => Promise<void | { message?: string }>;
  shareTagGroup?: (id: string, params: CloudShareParams) => Promise<CloudShareResponse>;

  // Bloggers
  getBloggers: (params?: { page?: number; limit?: number; all?: boolean }) => Promise<{
    bloggers: CloudBlogger[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  }>;
  deleteBlogger: (id: string) => Promise<void | { message?: string }>;
  updateBloggerTags: (id: string, tagIds: string[]) => Promise<{ blogger: CloudBlogger }>;
  updateBlogger?: (id: string, data: { annotation?: string }) => Promise<{ blogger: CloudBlogger }>;

  // Posts
  getPosts: (params?: { tagId?: string; tagGroupId?: string; bloggerId?: string; isStarred?: boolean; isVideo?: boolean; isPageClip?: boolean }) => Promise<{ posts: CloudPost[]; pagination?: any }>;
  deletePost: (id: string) => Promise<void | { message?: string }>;
  deletePosts: (ids: string[]) => Promise<{ message: string; deletedCount: number }>;
  toggleStarPost: (id: string) => Promise<{ post: CloudPost }>;
  togglePinPost: (id: string) => Promise<{ post: CloudPost }>;
  updatePostTags: (id: string, tagIds: string[]) => Promise<{ post: CloudPost }>;
}

// Re-export CloudShareParams and CloudShareResponse from apiClient for backward compatibility
export type { CloudShareParams, CloudShareResponse } from '../apiClient';

/**
 * 分享目标
 */
export interface ShareTarget {
  type: 'cloudTag' | 'cloudTagGroup';
  id: string;
  name: string;
  isPublic: boolean;
  alias?: string | null;
  description?: string | null;
  isAnonymous?: boolean;
  isEncrypted?: boolean;
}

export interface UseMyCloudClippingsPageReturn {
  // Loading states
  loading: boolean;
  loadingContent: boolean;

  // Data
  tags: CloudTag[];
  tagGroups: CloudTagGroup[];
  bloggers: CloudBlogger[];
  posts: CloudPost[];
  filteredPosts: CloudPost[];

  // Pagination
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMorePosts: () => Promise<void>;

  // Blogger Pagination
  hasMoreBloggers: boolean;
  isLoadingMoreBloggers: boolean;
  loadMoreBloggers: () => Promise<void>;
  loadAllBloggers: () => Promise<void>;
  totalBloggers: number;

  // View state
  currentView: CloudClippingsView;
  selectedTagId: string | null;
  selectedTagGroupId: string | null;
  selectedBloggerId: string | null;

  // Search & Sort
  searchKeyword: string;
  setSearchKeyword: (keyword: string) => void;
  sortBy: 'blogger' | 'time';
  setSortBy: (sortBy: 'blogger' | 'time') => void;

  // Tag Group expansion
  expandedTagGroups: Record<string, boolean>;
  toggleTagGroupExpand: (groupId: string) => void;

  // Post Detail Selection
  selectedPost: CloudPost | null;
  setSelectedPost: (post: CloudPost | null) => void;

  // Batch Selection (框选)
  selectedPostIds: Set<string>;
  isSelected: (postId: string) => boolean;
  toggleSelection: (postId: string) => void;
  selectPosts: (postIds: string[]) => void;
  addToSelection: (postIds: string[]) => void;
  clearSelection: () => void;
  selectedCount: number;

  // Tag Actions
  handleCreateTag: (name: string, groupId?: string) => Promise<void>;
  handleRenameTag: (id: string, name: string) => Promise<void>;
  handleDeleteTag: (id: string) => Promise<void>;
  handleReorderTags: (tagOrders: Array<{ id: string; order: number; groupId?: string | null }>) => void;
  handleUpdateTagGroupAssignment: (tagId: string, groupId: string | null, order: number) => void;

  // Tag Group Actions
  handleCreateTagGroup: (name: string) => Promise<void>;
  handleRenameTagGroup: (id: string, name: string) => Promise<void>;
  handleDeleteTagGroup: (id: string) => Promise<void>;
  handleReorderTagGroups: (groupOrders: Array<{ id: string; order: number }>) => void;

  // Blogger Actions
  handleDeleteBlogger: (id: string) => Promise<void>;
  handleUpdateBloggerTags: (id: string, tagIds: string[]) => Promise<void>;
  handleUpdateBloggerAnnotation: (id: string, annotation: string) => Promise<void>;

  // Post Actions
  handleDeletePost: (id: string) => Promise<void>;
  handleBatchDeletePosts: () => Promise<void>;
  handleToggleStar: (id: string) => Promise<void>;
  handleTogglePin: (id: string) => Promise<void>;
  handleUpdatePostTags: (id: string, tagIds: string[]) => Promise<void>;
  refreshPosts: (options?: { silent?: boolean }) => Promise<void>;

  // Navigation
  handleSelectView: (view: CloudClippingsView, id?: string) => void;

  // Share
  shareModalVisible: boolean;
  shareTarget: ShareTarget | null;
  handleOpenShareModal: (type: 'cloudTag' | 'cloudTagGroup', item: CloudTag | CloudTagGroup) => void;
  handleCloseShareModal: () => void;
  handleShare: (params: CloudShareParams) => Promise<void>;
  handleUnshare: () => Promise<void>;

  // Error handling
  error: Error | null;
  handleRetry: () => void;
}

// 解析 URL view 参数，返回初始状态
const parseUrlViewParam = (urlView?: string): {
  view: CloudClippingsView;
  tagId: string | null;
  tagGroupId: string | null;
  bloggerId: string | null;
} => {
  if (!urlView) {
    return { view: 'recent', tagId: null, tagGroupId: null, bloggerId: null };
  }

  if (urlView.startsWith('tag-')) {
    return { view: 'tag', tagId: urlView.replace('tag-', ''), tagGroupId: null, bloggerId: null };
  } else if (urlView.startsWith('group-')) {
    return { view: 'tagGroup', tagId: null, tagGroupId: urlView.replace('group-', ''), bloggerId: null };
  } else if (urlView === 'bloggers' || urlView.startsWith('bloggers/')) {
    const bloggerId = urlView.replace('bloggers/', '');
    return { view: 'bloggers', tagId: null, tagGroupId: null, bloggerId: bloggerId !== 'bloggers' ? bloggerId : null };
  } else if (['starred', 'videos', 'uncategorized'].includes(urlView)) {
    return { view: urlView as CloudClippingsView, tagId: null, tagGroupId: null, bloggerId: null };
  }
  return { view: 'recent', tagId: null, tagGroupId: null, bloggerId: null };
};

export const useMyCloudClippingsPage = (
  options: UseMyCloudClippingsPageOptions
): UseMyCloudClippingsPageReturn => {
  const { isLoggedIn, isInitializing, urlView, navigate, apiClient } = options;

  // 解析初始 URL 状态
  const initialState = parseUrlViewParam(urlView);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);

  // Data
  const [tags, setTags] = useState<CloudTag[]>([]);
  const [tagGroups, setTagGroups] = useState<CloudTagGroup[]>([]);
  const [bloggers, setBloggers] = useState<CloudBlogger[]>([]);
  const [posts, setPosts] = useState<CloudPost[]>([]);

  // Pagination state (for recent, starred, videos, uncategorized views)
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Blogger Pagination state
  const [hasMoreBloggers, setHasMoreBloggers] = useState(false);
  const [isLoadingMoreBloggers, setIsLoadingMoreBloggers] = useState(false);
  const [bloggerPage, setBloggerPage] = useState(1);
  const [totalBloggers, setTotalBloggers] = useState(0);
  const [allBloggersLoaded, setAllBloggersLoaded] = useState(false);

  // Refresh counter for forcing data reload
  const [refreshKey, setRefreshKey] = useState(0);

  // View state - 使用解析后的初始值
  const [currentView, setCurrentView] = useState<CloudClippingsView>(initialState.view);
  const [selectedTagId, setSelectedTagId] = useState<string | null>(initialState.tagId);
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<string | null>(initialState.tagGroupId);
  const [selectedBloggerId, setSelectedBloggerId] = useState<string | null>(initialState.bloggerId);

  // Search & Sort
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState<'blogger' | 'time'>('blogger');

  // Tag Group expansion
  const [expandedTagGroups, setExpandedTagGroups] = useState<Record<string, boolean>>({});

  // Post Detail Selection
  const [selectedPost, setSelectedPost] = useState<CloudPost | null>(null);

  // Batch Selection (框选)
  const {
    selectedPostIds,
    isSelected,
    toggleSelection,
    selectPosts,
    addToSelection,
    clearSelection,
    selectedCount,
  } = usePostSelection();

  // Error
  const [error, setError] = useState<Error | null>(null);

  // Share Modal
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);

  // ========== Debounced Sync 机制 ==========
  // 用于存储待同步的标签顺序变更
  const pendingTagOrdersRef = useRef<Map<string, { order: number; groupId?: string | null }>>(new Map());
  const tagSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 用于存储待同步的标签组顺序变更
  const pendingGroupOrdersRef = useRef<Map<string, number>>(new Map());
  const groupSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 同步标签顺序到云端
  const syncTagOrders = useCallback(async () => {
    if (pendingTagOrdersRef.current.size === 0) return;

    const tagOrders = Array.from(pendingTagOrdersRef.current.entries()).map(([id, data]) => ({
      id,
      order: data.order,
      groupId: data.groupId,
    }));

    // 清空待同步队列
    pendingTagOrdersRef.current.clear();

    try {
      await apiClient.reorderTags(tagOrders);
      console.log('[Cloud Clippings] Tags synced to cloud:', tagOrders.length);
    } catch (err: any) {
      console.error('[Cloud Clippings] Failed to sync tags:', err);
      message.error('同步标签顺序失败');
    }
  }, [apiClient]);

  // 同步标签组顺序到云端
  const syncGroupOrders = useCallback(async () => {
    if (pendingGroupOrdersRef.current.size === 0) return;

    const groupOrders = Array.from(pendingGroupOrdersRef.current.entries()).map(([id, order]) => ({
      id,
      order,
    }));

    // 清空待同步队列
    pendingGroupOrdersRef.current.clear();

    try {
      await apiClient.reorderTagGroups(groupOrders);
      console.log('[Cloud Clippings] Tag groups synced to cloud:', groupOrders.length);
    } catch (err: any) {
      console.error('[Cloud Clippings] Failed to sync tag groups:', err);
      message.error('同步标签组顺序失败');
    }
  }, [apiClient]);

  // 清理 timers
  useEffect(() => {
    return () => {
      if (tagSyncTimerRef.current) clearTimeout(tagSyncTimerRef.current);
      if (groupSyncTimerRef.current) clearTimeout(groupSyncTimerRef.current);
    };
  }, []);

  // 页面卸载前同步
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 立即同步未保存的变更
      if (pendingTagOrdersRef.current.size > 0) {
        syncTagOrders();
      }
      if (pendingGroupOrdersRef.current.size > 0) {
        syncGroupOrders();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [syncTagOrders, syncGroupOrders]);

  // Load initial data
  const loadInitialData = useCallback(async () => {
    if (isInitializing) return;
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [tagsResult, tagGroupsResult, bloggersResult] = await Promise.all([
        apiClient.getTags(),
        apiClient.getTagGroups(),
        apiClient.getBloggers({ page: 1, limit: 100 }),
      ]);

      setTags(tagsResult.tags || []);
      setTagGroups(tagGroupsResult.tagGroups || []);
      setBloggers(bloggersResult.bloggers || []);

      // Set blogger pagination state
      if (bloggersResult.pagination) {
        setHasMoreBloggers(bloggersResult.pagination.hasMore);
        setTotalBloggers(bloggersResult.pagination.total);
        setAllBloggersLoaded(!bloggersResult.pagination.hasMore);
        setBloggerPage(1);
      }

      // Initialize expanded state
      const expanded: Record<string, boolean> = {};
      (tagGroupsResult.tagGroups || []).forEach((g: CloudTagGroup) => {
        expanded[g.id] = g.isExpanded;
      });
      setExpandedTagGroups(expanded);
    } catch (err) {
      console.error('Failed to load cloud clippings data:', err);
      setError(err as Error);
      message.error('加载数据失败，请刷新重试');
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, isInitializing, apiClient]);

  // Load bloggers (with pagination support)
  const loadBloggers = useCallback(async (options?: { page?: number; loadAll?: boolean; silent?: boolean }) => {
    if (!isLoggedIn) return;

    const page = options?.page || 1;
    const loadAll = options?.loadAll || false;

    if (!options?.silent) {
      if (page === 1) {
        setLoadingContent(true);
      } else {
        setIsLoadingMoreBloggers(true);
      }
    }

    try {
      const result = await apiClient.getBloggers({
        page,
        limit: 100,
        all: loadAll,
      });

      if (page === 1 || loadAll) {
        setBloggers(result.bloggers || []);
      } else {
        setBloggers(prev => [...prev, ...(result.bloggers || [])]);
      }

      if (result.pagination) {
        setHasMoreBloggers(result.pagination.hasMore);
        setTotalBloggers(result.pagination.total);
        setAllBloggersLoaded(loadAll || !result.pagination.hasMore);
        setBloggerPage(page);
      }
    } catch (err) {
      console.error('Failed to load bloggers:', err);
    } finally {
      if (!options?.silent) {
        setLoadingContent(false);
        setIsLoadingMoreBloggers(false);
      }
    }
  }, [isLoggedIn, apiClient]);

  // Load more bloggers (for scroll pagination)
  const loadMoreBloggers = useCallback(async () => {
    if (isLoadingMoreBloggers || !hasMoreBloggers || allBloggersLoaded) return;
    await loadBloggers({ page: bloggerPage + 1 });
  }, [isLoadingMoreBloggers, hasMoreBloggers, allBloggersLoaded, bloggerPage, loadBloggers]);

  // Load all bloggers (for search/sort)
  const loadAllBloggers = useCallback(async () => {
    if (allBloggersLoaded) return;
    await loadBloggers({ loadAll: true });
  }, [allBloggersLoaded, loadBloggers]);

  // Load posts based on current view
  const loadPosts = useCallback(async (options?: { silent?: boolean }) => {
    if (!isLoggedIn) return;

    if (!options?.silent) {
      setLoadingContent(true);
    }
    // Reset pagination state
    setCurrentPage(1);
    setHasMore(false);

    try {
      const params: any = {};

      // Determine pagination limit based on view
      const isPaginatedView = ['recent', 'starred', 'videos', 'uncategorized'].includes(currentView);
      if (isPaginatedView) {
        params.limit = 50;
        params.page = 1;
      }

      if (currentView === 'tag' && selectedTagId) {
        params.tagId = selectedTagId;
      } else if (currentView === 'tagGroup' && selectedTagGroupId) {
        params.tagGroupId = selectedTagGroupId;
      } else if (currentView === 'bloggers' && selectedBloggerId) {
        params.bloggerId = selectedBloggerId;
      } else if (currentView === 'starred') {
        params.isStarred = true;
      } else if (currentView === 'videos') {
        params.isVideo = true;
      } else if (currentView === 'uncategorized') {
        // 未分类：没有任何标签的帖子（与本地 Electron 行为一致）
        params.noTags = true;
      }

      const result = await apiClient.getPosts(params);
      setPosts(result.posts || []);

      // Set hasMore based on pagination info (if available)
      if (result.pagination) {
        setHasMore(result.pagination.page < result.pagination.totalPages);
      } else if (isPaginatedView) {
        // For paginated views without pagination info, check if we got full page
        setHasMore((result.posts || []).length >= 50);
      }
    } catch (err) {
      console.error('Failed to load posts:', err);
      message.error('加载帖子失败');
    } finally {
      if (!options?.silent) {
        setLoadingContent(false);
      }
    }
  }, [isLoggedIn, currentView, selectedTagId, selectedTagGroupId, selectedBloggerId, apiClient]);

  // Load more posts (for pagination)
  const loadMorePosts = useCallback(async () => {
    if (!isLoggedIn || isLoadingMore || !hasMore) return;

    // Only paginated views support load more
    const isPaginatedView = ['recent', 'starred', 'videos', 'uncategorized'].includes(currentView);
    if (!isPaginatedView) return;

    // recent 页限制最多加载 150 条
    const RECENT_MAX_POSTS = 150;
    if (currentView === 'recent' && posts.length >= RECENT_MAX_POSTS) {
      setHasMore(false);
      return;
    }

    setIsLoadingMore(true);

    try {
      const params: any = {
        limit: 50,
        page: currentPage + 1,
      };

      if (currentView === 'starred') {
        params.isStarred = true;
      } else if (currentView === 'videos') {
        params.isVideo = true;
      } else if (currentView === 'uncategorized') {
        params.noTags = true;
      }

      const result = await apiClient.getPosts(params);

      if (result.posts && result.posts.length > 0) {
        setPosts(prev => [...prev, ...result.posts]);
        setCurrentPage(prev => prev + 1);

        // Update hasMore
        const newTotalCount = posts.length + result.posts.length;
        if (currentView === 'recent' && newTotalCount >= RECENT_MAX_POSTS) {
          // recent 页达到 150 条限制
          setHasMore(false);
        } else if (result.pagination) {
          setHasMore(result.pagination.page < result.pagination.totalPages);
        } else {
          setHasMore(result.posts.length >= 50);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load more posts:', err);
      message.error('加载更多失败');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoggedIn, currentView, currentPage, isLoadingMore, hasMore, posts.length, apiClient]);

  // Filter posts by search keyword
  const filteredPosts = useMemo(() => {
    if (!searchKeyword.trim()) return posts;

    const keyword = searchKeyword.toLowerCase();
    return posts.filter(
      (post) =>
        post.title?.toLowerCase().includes(keyword) ||
        post.content?.toLowerCase().includes(keyword) ||
        post.blogger?.nickname?.toLowerCase().includes(keyword)
    );
  }, [posts, searchKeyword]);

  // Toggle tag group expand
  const toggleTagGroupExpand = useCallback((groupId: string) => {
    setExpandedTagGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, []);

  // Navigation
  const handleSelectView = useCallback(
    (view: CloudClippingsView, id?: string) => {
      let path = '/cloud-clippings';

      if (view === 'tag' && id) {
        path = `/cloud-clippings/tag-${id}`;
      } else if (view === 'tagGroup' && id) {
        path = `/cloud-clippings/group-${id}`;
      } else if (view === 'bloggers') {
        path = `/cloud-clippings/bloggers${id ? `/${id}` : ''}`;
        // Force refresh when selecting bloggers view (even if already on it)
        setRefreshKey(prev => prev + 1);
      } else if (view !== 'recent') {
        path = `/cloud-clippings/${view}`;
      }

      navigate(path);
    },
    [navigate]
  );

  // Tag Actions
  const handleCreateTag = useCallback(
    async (name: string, groupId?: string) => {
      try {
        const result = await apiClient.createTag({ name, groupId });
        setTags((prev) => [...prev, result.tag]);
        message.success('标签创建成功');
      } catch (err: any) {
        message.error(err?.message || '创建标签失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleRenameTag = useCallback(
    async (id: string, name: string) => {
      try {
        const result = await apiClient.updateTag(id, { name });
        setTags((prev) => prev.map((t) => (t.id === id ? result.tag : t)));
        message.success('标签已重命名');
      } catch (err: any) {
        message.error(err?.message || '重命名失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleDeleteTag = useCallback(
    async (id: string) => {
      try {
        await apiClient.deleteTag(id);
        setTags((prev) => prev.filter((t) => t.id !== id));
        message.success('标签已删除');
      } catch (err: any) {
        message.error(err?.message || '删除失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleReorderTags = useCallback(
    (tagOrders: Array<{ id: string; order: number; groupId?: string | null }>) => {
      // 1. 立即更新本地状态（乐观更新）
      const orderMap = new Map(tagOrders.map((item) => [item.id, item]));
      setTags((prev) =>
        [...prev]
          .map((t) => {
            const update = orderMap.get(t.id);
            if (update) {
              return {
                ...t,
                order: update.order,
                groupId: update.groupId !== undefined ? update.groupId : t.groupId,
              };
            }
            return t;
          })
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );

      // 2. 将变更加入待同步队列
      tagOrders.forEach((item) => {
        pendingTagOrdersRef.current.set(item.id, {
          order: item.order,
          groupId: item.groupId,
        });
      });

      // 3. 重置防抖计时器
      if (tagSyncTimerRef.current) {
        clearTimeout(tagSyncTimerRef.current);
      }
      tagSyncTimerRef.current = setTimeout(() => {
        syncTagOrders();
      }, SYNC_DEBOUNCE_DELAY);
    },
    [syncTagOrders]
  );

  /**
   * 更新标签的分组分配（用于拖拽移入/移出分组）
   */
  const handleUpdateTagGroupAssignment = useCallback(
    (tagId: string, groupId: string | null, order: number) => {
      // 1. 立即更新本地状态（乐观更新）
      setTags((prev) =>
        prev.map((t) =>
          t.id === tagId ? { ...t, groupId, order } : t
        )
      );

      // 2. 将变更加入待同步队列
      pendingTagOrdersRef.current.set(tagId, { order, groupId });

      // 3. 重置防抖计时器
      if (tagSyncTimerRef.current) {
        clearTimeout(tagSyncTimerRef.current);
      }
      tagSyncTimerRef.current = setTimeout(() => {
        syncTagOrders();
      }, SYNC_DEBOUNCE_DELAY);
    },
    [syncTagOrders]
  );

  // Tag Group Actions
  const handleCreateTagGroup = useCallback(
    async (name: string) => {
      try {
        const result = await apiClient.createTagGroup({ name });
        setTagGroups((prev) => [...prev, result.tagGroup]);
        setExpandedTagGroups((prev) => ({ ...prev, [result.tagGroup.id]: true }));
        message.success('标签组创建成功');
      } catch (err: any) {
        message.error(err?.message || '创建标签组失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleRenameTagGroup = useCallback(
    async (id: string, name: string) => {
      try {
        const result = await apiClient.updateTagGroup(id, { name });
        setTagGroups((prev) => prev.map((g) => (g.id === id ? result.tagGroup : g)));
        message.success('标签组已重命名');
      } catch (err: any) {
        message.error(err?.message || '重命名失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleDeleteTagGroup = useCallback(
    async (id: string) => {
      try {
        await apiClient.deleteTagGroup(id);
        setTagGroups((prev) => prev.filter((g) => g.id !== id));
        // Move tags from deleted group to ungrouped
        setTags((prev) =>
          prev.map((t) => (t.groupId === id ? { ...t, groupId: null, group: null } : t))
        );
        message.success('标签组已删除');
      } catch (err: any) {
        message.error(err?.message || '删除失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleReorderTagGroups = useCallback(
    (groupOrders: Array<{ id: string; order: number }>) => {
      // 1. 立即更新本地状态（乐观更新）
      const orderMap = new Map(groupOrders.map((item) => [item.id, item.order]));
      setTagGroups((prev) =>
        [...prev]
          .map((g) => ({
            ...g,
            order: orderMap.has(g.id) ? orderMap.get(g.id)! : g.order,
          }))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );

      // 2. 将变更加入待同步队列
      groupOrders.forEach((item) => {
        pendingGroupOrdersRef.current.set(item.id, item.order);
      });

      // 3. 重置防抖计时器
      if (groupSyncTimerRef.current) {
        clearTimeout(groupSyncTimerRef.current);
      }
      groupSyncTimerRef.current = setTimeout(() => {
        syncGroupOrders();
      }, SYNC_DEBOUNCE_DELAY);
    },
    [syncGroupOrders]
  );

  // Blogger Actions
  const handleDeleteBlogger = useCallback(
    async (id: string) => {
      try {
        await apiClient.deleteBlogger(id);
        setBloggers((prev) => prev.filter((b) => b.id !== id));
        // Also remove posts from this blogger
        setPosts((prev) => prev.filter((p) => p.bloggerId !== id));
        message.success('博主已删除');
      } catch (err: any) {
        message.error(err?.message || '删除失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleUpdateBloggerTags = useCallback(
    async (id: string, tagIds: string[]) => {
      try {
        const result = await apiClient.updateBloggerTags(id, tagIds);
        setBloggers((prev) => prev.map((b) => (b.id === id ? result.blogger : b)));
        // 博主标签更新会同步到帖子，需要重新加载帖子列表
        await loadPosts();
        message.success('博主标签已更新');
      } catch (err: any) {
        message.error(err?.message || '更新失败');
        throw err;
      }
    },
    [apiClient, loadPosts]
  );

  const handleUpdateBloggerAnnotation = useCallback(
    async (id: string, annotation: string) => {
      if (!apiClient.updateBlogger) {
        message.error('此功能暂不可用');
        return;
      }
      try {
        const result = await apiClient.updateBlogger(id, { annotation: annotation || undefined });
        setBloggers((prev) => prev.map((b) => (b.id === id ? result.blogger : b)));
        message.success('备注已更新');
      } catch (err: any) {
        message.error(err?.message || '更新失败');
        throw err;
      }
    },
    [apiClient]
  );

  // Post Actions
  const handleDeletePost = useCallback(
    async (id: string) => {
      try {
        await apiClient.deletePost(id);
        setPosts((prev) => prev.filter((p) => p.id !== id));
        message.success('帖子已删除');
      } catch (err: any) {
        message.error(err?.message || '删除失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleBatchDeletePosts = useCallback(async () => {
    if (selectedCount === 0) return;

    try {
      const idsToDelete = Array.from(selectedPostIds);
      await apiClient.deletePosts(idsToDelete);
      // 更新本地状态
      setPosts((prev) => prev.filter((p) => !selectedPostIds.has(p.id)));
      // 清空选择
      clearSelection();
      message.success(`成功删除 ${idsToDelete.length} 个帖子`);
    } catch (err: any) {
      message.error(err?.message || '批量删除失败');
      throw err;
    }
  }, [apiClient, selectedPostIds, selectedCount, clearSelection]);

  const handleToggleStar = useCallback(
    async (id: string) => {
      try {
        const result = await apiClient.toggleStarPost(id);
        setPosts((prev) => prev.map((p) => (p.id === id ? result.post : p)));
      } catch (err: any) {
        message.error(err?.message || '操作失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleTogglePin = useCallback(
    async (id: string) => {
      try {
        const result = await apiClient.togglePinPost(id);
        setPosts((prev) => prev.map((p) => (p.id === id ? result.post : p)));
      } catch (err: any) {
        message.error(err?.message || '操作失败');
        throw err;
      }
    },
    [apiClient]
  );

  const handleUpdatePostTags = useCallback(
    async (id: string, tagIds: string[]) => {
      try {
        const result = await apiClient.updatePostTags(id, tagIds);
        setPosts((prev) => {
          // 如果当前是"未分类"视图，且帖子添加了标签，则从列表中移除
          if (currentView === 'uncategorized' && tagIds.length > 0) {
            return prev.filter((p) => p.id !== id);
          }
          // 否则正常更新帖子
          return prev.map((p) => (p.id === id ? result.post : p));
        });
        message.success('帖子标签已更新');
      } catch (err: any) {
        message.error(err?.message || '更新失败');
        throw err;
      }
    },
    [apiClient, currentView]
  );

  // ========== Share Handlers ==========

  const handleOpenShareModal = useCallback(
    (type: 'cloudTag' | 'cloudTagGroup', item: CloudTag | CloudTagGroup) => {
      setShareTarget({
        type,
        id: item.id,
        name: item.name,
        isPublic: item.isPublic || false,
        alias: item.alias,
        description: item.description,
        isAnonymous: item.isAnonymous,
        isEncrypted: item.isEncrypted,
      });
      setShareModalVisible(true);
    },
    []
  );

  const handleCloseShareModal = useCallback(() => {
    setShareModalVisible(false);
    setShareTarget(null);
  }, []);

  const handleShare = useCallback(
    async (params: CloudShareParams) => {
      if (!shareTarget) return;

      const { type, id } = shareTarget;

      try {
        if (type === 'cloudTag' && apiClient.shareTag) {
          const result = await apiClient.shareTag(id, params);
          if (result.tag) {
            setTags((prev) => prev.map((t) => (t.id === id ? result.tag! : t)));
          }
          const actionText = result.action === 'shared' ? '分享成功' : '更新成功';
          message.success(actionText);
        } else if (type === 'cloudTagGroup' && apiClient.shareTagGroup) {
          const result = await apiClient.shareTagGroup(id, params);
          if (result.tagGroup) {
            setTagGroups((prev) => prev.map((g) => (g.id === id ? result.tagGroup! : g)));
          }
          const actionText = result.action === 'shared' ? '分享成功' : '更新成功';
          message.success(actionText);
        } else {
          throw new Error('分享功能不可用');
        }
      } catch (err: any) {
        console.error('[Cloud Clippings] Share failed:', err);
        message.error(err?.message || '分享失败');
        throw err;
      }
    },
    [shareTarget, apiClient]
  );

  const handleUnshare = useCallback(async () => {
    if (!shareTarget) return;

    const { type, id } = shareTarget;

    try {
      const params: CloudShareParams = { isPublic: false };

      if (type === 'cloudTag' && apiClient.shareTag) {
        const result = await apiClient.shareTag(id, params);
        if (result.tag) {
          setTags((prev) => prev.map((t) => (t.id === id ? result.tag! : t)));
        }
        message.success('已取消分享');
      } else if (type === 'cloudTagGroup' && apiClient.shareTagGroup) {
        const result = await apiClient.shareTagGroup(id, params);
        if (result.tagGroup) {
          setTagGroups((prev) => prev.map((g) => (g.id === id ? result.tagGroup! : g)));
        }
        message.success('已取消分享');
      } else {
        throw new Error('取消分享功能不可用');
      }
    } catch (err: any) {
      console.error('[Cloud Clippings] Unshare failed:', err);
      message.error(err?.message || '取消分享失败');
      throw err;
    }
  }, [shareTarget, apiClient]);

  // Retry
  const handleRetry = useCallback(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Parse URL view parameter
  useEffect(() => {
    let newView: CloudClippingsView = 'recent';
    let newTagId: string | null = null;
    let newTagGroupId: string | null = null;
    let newBloggerId: string | null = null;

    if (!urlView) {
      // 默认显示最近剪藏
    } else if (urlView.startsWith('tag-')) {
      newView = 'tag';
      newTagId = urlView.replace('tag-', '');
    } else if (urlView.startsWith('group-')) {
      newView = 'tagGroup';
      newTagGroupId = urlView.replace('group-', '');
    } else if (urlView === 'bloggers' || urlView.startsWith('bloggers/')) {
      newView = 'bloggers';
      const bloggerId = urlView.replace('bloggers/', '');
      newBloggerId = bloggerId !== 'bloggers' ? bloggerId : null;
    } else if (['starred', 'videos', 'uncategorized'].includes(urlView)) {
      newView = urlView as CloudClippingsView;
    }
    // else: default 'recent'

    setCurrentView(newView);
    setSelectedTagId(newTagId);
    setSelectedTagGroupId(newTagGroupId);
    setSelectedBloggerId(newBloggerId);

    // 特殊视图强制使用时间排序
    if (shouldForceTimeSort(newView)) {
      setSortBy('time');
    }
  }, [urlView]);

  // Initial load
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load posts when view changes (or refresh bloggers when switching to bloggers view)
  useEffect(() => {
    if (!loading) {
      if (currentView === 'bloggers' && !selectedBloggerId) {
        // When switching to bloggers view, refresh bloggers data
        loadBloggers({ page: 1 });
      } else {
        loadPosts();
      }
    }
  }, [loadPosts, loadBloggers, loading, currentView, selectedTagId, selectedTagGroupId, selectedBloggerId, refreshKey]);

  return {
    // Loading states
    loading,
    loadingContent,

    // Data
    tags,
    tagGroups,
    bloggers,
    posts,
    filteredPosts,

    // Pagination
    hasMore,
    isLoadingMore,
    loadMorePosts,

    // Blogger Pagination
    hasMoreBloggers,
    isLoadingMoreBloggers,
    loadMoreBloggers,
    loadAllBloggers,
    totalBloggers,

    // View state
    currentView,
    selectedTagId,
    selectedTagGroupId,
    selectedBloggerId,

    // Search & Sort
    searchKeyword,
    setSearchKeyword,
    sortBy,
    setSortBy,

    // Tag Group expansion
    expandedTagGroups,
    toggleTagGroupExpand,

    // Post Detail Selection
    selectedPost,
    setSelectedPost,

    // Batch Selection (框选)
    selectedPostIds,
    isSelected,
    toggleSelection,
    selectPosts,
    addToSelection,
    clearSelection,
    selectedCount,

    // Tag Actions
    handleCreateTag,
    handleRenameTag,
    handleDeleteTag,
    handleReorderTags,
    handleUpdateTagGroupAssignment,

    // Tag Group Actions
    handleCreateTagGroup,
    handleRenameTagGroup,
    handleDeleteTagGroup,
    handleReorderTagGroups,

    // Blogger Actions
    handleDeleteBlogger,
    handleUpdateBloggerTags,
    handleUpdateBloggerAnnotation,

    // Post Actions
    handleDeletePost,
    handleBatchDeletePosts,
    handleToggleStar,
    handleTogglePin,
    handleUpdatePostTags,
    refreshPosts: loadPosts,

    // Navigation
    handleSelectView,

    // Share
    shareModalVisible,
    shareTarget,
    handleOpenShareModal,
    handleCloseShareModal,
    handleShare,
    handleUnshare,

    // Error handling
    error,
    handleRetry,
  };
};
